import Button from 'components/Button';
import DateTimeSelect from 'components/Form/DateTimeSelect';
import Dropdown from 'components/Form/Dropdown';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import TooltipWrapper from 'components/Tooltip';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {FC, useCallback, useMemo} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {
    fromUnixTimestamp,
    setToEndOfDay,
    setToStartOfDay,
    toUnixTimestamp,
} from 'utils/date';
import {setUrl} from 'utils/request';

export interface FilterFormData {
    documentStatus: string;
    documentType: string;
    receivedDateFrom: Date | undefined;
    receivedDateTo: Date | undefined;
    searchTerms: string;
}

interface FilterFormProps {
    flags: {
        portalAllowDocumentFilter?: boolean;
    };
    url: string;
}

const documentStatusDropdownOptions = [
    'all',
    'valid',
    'cancelled',
    'inaccessible',
];

const FilterForm: FC<FilterFormProps> = ({flags, url}) => {
    const {t} = useTranslation(['Portal', 'Document']);

    // Pulls values from query parameters if they exist
    const initialValues: FilterFormData = useMemo(() => {
        const searchParams = new URLSearchParams(window.location.search);

        return {
            documentStatus: searchParams.get('document_status') || 'valid',
            documentType: searchParams.get('document_type') || '',
            receivedDateFrom:
                fromUnixTimestamp(searchParams.get('received_date_from')) ||
                undefined,
            receivedDateTo:
                fromUnixTimestamp(searchParams.get('received_date_to')) ||
                undefined,
            searchTerms: searchParams.get('search_terms') || '',
        };
    }, []);

    const isFormClean: boolean = useMemo(
        () =>
            !(
                initialValues.documentStatus === 'valid' &&
                initialValues.documentType === '' &&
                initialValues.searchTerms === '' &&
                initialValues.receivedDateFrom === undefined &&
                initialValues.receivedDateTo === undefined
            ),
        [initialValues],
    );

    const methods = useForm<FilterFormData>({
        defaultValues: initialValues,
    });
    const {handleSubmit} = methods;

    // Sets the URL and query parameters and loads the URL
    const onSubmit = useCallback(
        ({
            documentStatus,
            documentType,
            receivedDateFrom,
            receivedDateTo,
            searchTerms,
        }: FilterFormData) => {
            const builtUrl = new URL(url, window.location.origin);
            const queryParameters = new URLSearchParams(builtUrl.search);

            if (documentStatus) {
                queryParameters.append('document_status', documentStatus);
            }

            if (documentType) {
                queryParameters.append('document_type', documentType);
            }

            if (receivedDateFrom) {
                queryParameters.append(
                    'received_date_from',
                    toUnixTimestamp(setToStartOfDay(receivedDateFrom)),
                );
            }

            if (receivedDateTo) {
                queryParameters.append(
                    'received_date_to',
                    toUnixTimestamp(setToEndOfDay(receivedDateTo)),
                );
            }

            if (searchTerms) {
                queryParameters.append('search_terms', searchTerms);
            }

            builtUrl.search = queryParameters.toString();
            setUrl(builtUrl.toString());
        },
        [url],
    );

    const documentTypeDropdownOptions = [];

    documentTypeDropdownOptions.push(
        {label: t('filters.all'), value: ''},
        ...[
            'order',
            'quote',
            'orderslip',
            'orderconfirmation',
            'timesheet',
            'deliveryslip',
            'acceptance',
            'paymentnotice',
            'invoice',
            'receipt',
        ].map((documentType) => ({
            label: t(`documentTypes.${documentType}`, {ns: 'Document'}),
            value: documentType,
        })),
        {label: t('filters.zip'), value: 'zip'},
        {label: t('filters.pdf'), value: 'pdf'},
    );

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="flex flex-row space-x-2">
                    <div className="flex">
                        <Dropdown
                            aria-label="Document status"
                            className="mt-4 inline-block w-52 leading-6"
                            hasClearSelection={false}
                            label={t('filters.documentStatus')}
                            name="documentStatus"
                            options={documentStatusDropdownOptions.map(
                                (option) => ({
                                    label: t(
                                        `filters.documentStatuses.${option}`,
                                    ),
                                    value: option,
                                }),
                            )}
                            type="select"
                        />
                    </div>
                    {flags.portalAllowDocumentFilter && (
                        <>
                            <Dropdown
                                aria-label="search terms"
                                className="mt-4 w-[186px] leading-6"
                                hasClearSelection={false}
                                label={
                                    <span className="flex items-center gap-x-1">
                                        {t('filters.documentType')}
                                        <TooltipIcon
                                            message={t(
                                                'filters.documentTypeTooltip',
                                            )}
                                            size="base"
                                        />
                                    </span>
                                }
                                name="documentType"
                                options={documentTypeDropdownOptions}
                                required={false}
                                type="select"
                            />
                            <InputText
                                aria-label="search terms"
                                className="mt-4 inline-block"
                                label={
                                    <span className="flex items-center gap-x-1">
                                        {t('filters.searchTerms')}
                                        <TooltipIcon
                                            href="https://help.makeleaps.jp/ja/articles/8687205#h_f03b498b79"
                                            message={t(
                                                'filters.searchTermsTooltip',
                                            )}
                                            size="base"
                                        />
                                    </span>
                                }
                                name="searchTerms"
                            />
                            <div className="flex">
                                <DateTimeSelect
                                    aria-label="Received date from"
                                    className="mt-4 w-36"
                                    label={t('filters.receivedDate')}
                                    name="receivedDateFrom"
                                    showTime={false}
                                />
                                <span className="mx-1 mt-10 inline-block">
                                    <span className="relative top-[7px]">
                                        &ndash;
                                    </span>
                                </span>
                                <DateTimeSelect
                                    aria-label="Received date end"
                                    className="mt-4 w-36"
                                    label="&nbsp;"
                                    name="receivedDateTo"
                                    showTime={false}
                                />
                            </div>
                        </>
                    )}
                    <TooltipWrapper message={t('search')}>
                        <SubmitButton
                            aria-label="Search"
                            className="mt-10"
                            icon="search"
                            kind="secondary"
                            name="search"
                            onClick={() => {
                                handleSubmit(onSubmit);
                            }}
                        />
                    </TooltipWrapper>
                    {isFormClean && (
                        <TooltipWrapper message={t('filters.clear')}>
                            <Button
                                aria-label="Clear"
                                className="mt-10"
                                icon="close"
                                kind="secondary"
                                name="clear"
                                onClick={() => {
                                    setUrl(
                                        window.location
                                            .toString()
                                            .replace(
                                                window.location.search,
                                                '',
                                            ),
                                    );
                                }}
                            />
                        </TooltipWrapper>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

export default FilterForm;
